import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Settings, Building2, Save, ScanLine, ImageIcon, Trash2, Download, AlertTriangle, CheckSquare, Mail, Send, CheckCircle2, XCircle } from 'lucide-react';
import { systemSettingsApi } from '../lib/api';
import api from '../lib/api';
import { toast } from 'sonner';

const SystemSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showWipeDialog, setShowWipeDialog] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState('');
  const [wipeProducts, setWipeProducts] = useState(false);
  const [wipeOffices, setWipeOffices] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Email service status
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailTestTo, setEmailTestTo] = useState('');
  const [emailTestBusy, setEmailTestBusy] = useState(false);

  useEffect(() => { load(); loadEmailStatus(); }, []);

  const loadEmailStatus = async () => {
    try {
      const r = await api.get('/auth/email-service-status');
      setEmailStatus(r.data);
    } catch { /* admins only — silently ignore */ }
  };

  const handleEmailTest = async () => {
    if (!emailTestTo || !emailTestTo.includes('@')) {
      toast.error('Enter a valid target email');
      return;
    }
    setEmailTestBusy(true);
    try {
      const r = await api.post('/auth/email-service-test', { to: emailTestTo });
      if (r.data.success) {
        toast.success(`Test email sent to ${emailTestTo}. Check inbox (and spam folder).`);
      } else {
        toast.error(`Test failed: ${r.data.reason || 'unknown'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    }
    setEmailTestBusy(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await systemSettingsApi.get();
      setSettings(res.data);
      if (res.data.company_logo && res.data.company_logo.length > 10) setLogoPreview(res.data.company_logo);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be under 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setLogoPreview(dataUrl);
      setSettings(prev => ({ ...prev, company_logo: dataUrl }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = async () => {
    setLogoPreview(null);
    const updated = { ...settings, company_logo: "" };
    setSettings(updated);
    // Auto-save the removal immediately
    try {
      await systemSettingsApi.update(updated);
      toast.success('Logo removed');
    } catch (e) {
      toast.error('Failed to remove logo');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await systemSettingsApi.update(settings);
      toast.success('Settings saved successfully');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    }
    setSaving(false);
  };

  const handleExportDatabase = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/database-export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `CBS_Database_Export_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Database exported successfully');
    } catch (e) {
      toast.error('Failed to export database');
    }
    setExporting(false);
  };

  const handleWipeDatabase = async () => {
    if (wipeConfirm !== 'CONFIRM WIPE') {
      toast.error('Please type CONFIRM WIPE exactly to proceed');
      return;
    }
    setWiping(true);
    try {
      const res = await api.post('/admin/database-wipe', {
        confirmation: 'CONFIRM WIPE',
        wipe_products: wipeProducts,
        wipe_offices: wipeOffices,
      });
      toast.success(res.data.message);
      setShowWipeDialog(false);
      setWipeConfirm('');
      setWipeProducts(false);
      setWipeOffices(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to wipe database');
    }
    setWiping(false);
  };

  if (loading) return <div className="p-6 text-center py-16 text-gray-400">Loading settings...</div>;
  if (!settings) return <div className="p-6 text-center py-16 text-red-500">Failed to load settings</div>;

  return (
    <div className="space-y-6 fade-in" data-testid="system-settings-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Admin</span><span>/</span><span>System</span><span>/</span><span className="text-[#1f1f1f] font-medium">Settings</span>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">CONFIGURATION</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">System Settings</h1>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Organization Details */}
      <div className="bg-white border border-gray-200 p-6" data-testid="org-details-section">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-5 h-5 text-[#C5A059]" />
          <h3 className="font-semibold text-[#1f1f1f]">Organization Details</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">These details appear on the login page and system-wide.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Organization Name</Label>
            <Input
              value={settings.organization_name || ''}
              onChange={(e) => setSettings({...settings, organization_name: e.target.value})}
              placeholder="e.g., Nkosi Financial Services"
              data-testid="org-name-input"
            />
            <p className="text-[10px] text-gray-400 mt-1">Displayed on the login page header</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Tagline</Label>
            <Input
              value={settings.organization_tagline || ''}
              onChange={(e) => setSettings({...settings, organization_tagline: e.target.value})}
              placeholder="e.g., Empowering communities through finance"
              data-testid="org-tagline-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Contact Email</Label>
            <Input
              type="email"
              value={settings.contact_email || ''}
              onChange={(e) => setSettings({...settings, contact_email: e.target.value})}
              placeholder="info@example.com"
              data-testid="contact-email-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Contact Phone</Label>
            <Input
              value={settings.contact_phone || ''}
              onChange={(e) => setSettings({...settings, contact_phone: e.target.value})}
              placeholder="+27 11 123 4567"
              data-testid="contact-phone-input"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-gray-500">Physical Address</Label>
            <Input
              value={settings.address || ''}
              onChange={(e) => setSettings({...settings, address: e.target.value})}
              placeholder="123 Main Street, Johannesburg"
              data-testid="address-input"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-gray-500">Company Logo</Label>
            <p className="text-[10px] text-gray-400 mb-2">Upload a logo (PNG, JPG, max 2MB). Appears on the login page next to the company name.</p>
            <div className="flex items-center gap-4">
              {logoPreview ? (
                <div className="flex items-center gap-3">
                  <img src={logoPreview} alt="Company logo" className="h-12 w-auto max-w-[120px] object-contain border border-gray-200 rounded p-1 bg-white" data-testid="logo-preview" />
                  <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200" onClick={handleRemoveLogo} data-testid="remove-logo-btn">Remove</Button>
                </div>
              ) : (
                <div className="h-12 w-12 flex items-center justify-center border border-dashed border-gray-300 rounded bg-gray-50">
                  <ImageIcon className="w-5 h-5 text-gray-300" />
                </div>
              )}
              <Input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={handleLogoUpload} className="text-xs max-w-[250px]" data-testid="logo-upload-input" />
            </div>
          </div>
        </div>
      </div>

      {/* OCR Document Scanning */}
      <div className="bg-white border border-gray-200 p-6" data-testid="ocr-scanning-section">
        <div className="flex items-center gap-2 mb-4">
          <ScanLine className="w-5 h-5 text-[#C5A059]" />
          <h3 className="font-semibold text-[#1f1f1f]">OCR Document Scanning</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">Enable AI-powered document scanning to auto-populate client fields from ID documents and payslips. This feature uses GPT-4o Vision and incurs a small cost per scan.</p>
        <div className="flex items-center gap-3">
          <Switch
            checked={settings.ocr_scanning_enabled || false}
            onCheckedChange={(v) => setSettings({...settings, ocr_scanning_enabled: v})}
            data-testid="ocr-scanning-toggle"
          />
          <span className="text-sm text-gray-600">{settings.ocr_scanning_enabled ? 'Enabled' : 'Disabled'}</span>
        </div>
        {settings.ocr_scanning_enabled && (
          <p className="text-xs text-amber-600 mt-3 bg-amber-50 px-3 py-2 rounded">Note: Each document scan incurs a small API cost. Ensure your balance is topped up under Profile &rarr; Universal Key &rarr; Add Balance.</p>
        )}
      </div>

      {/* Display Theme */}
      <div className="bg-white border border-gray-200 p-6" data-testid="display-theme-section">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="w-5 h-5 text-[#C5A059]" />
          <h3 className="font-semibold text-[#1f1f1f]">Display Theme</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">Choose the app's visual appearance. Changes apply immediately and are saved for all users.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => {
              setSettings({...settings, display_theme: 'gold'});
              document.documentElement.removeAttribute('data-theme');
            }}
            className={`p-4 border-2 rounded-lg text-left transition-all ${
              (!settings.display_theme || settings.display_theme === 'gold')
                ? 'border-[#C5A059] bg-amber-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            data-testid="theme-gold-btn"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="flex gap-1">
                <div className="w-6 h-6 rounded bg-[#1f1f1f]"></div>
                <div className="w-6 h-6 rounded bg-[#C5A059]"></div>
                <div className="w-6 h-6 rounded bg-[#333333]"></div>
              </div>
              <span className="font-semibold text-sm">Gold / Default</span>
              {(!settings.display_theme || settings.display_theme === 'gold') && <span className="ml-auto text-xs text-[#C5A059] font-semibold">Active</span>}
            </div>
            <p className="text-xs text-gray-500">Dark bronze header (#1f1f1f) with gold accents (#C5A059). Warm, professional look.</p>
          </button>
          <button
            onClick={() => {
              setSettings({...settings, display_theme: 'navy'});
              document.documentElement.setAttribute('data-theme', 'navy');
            }}
            className={`p-4 border-2 rounded-lg text-left transition-all ${
              settings.display_theme === 'navy'
                ? 'border-[#0D1B2A] bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            data-testid="theme-navy-btn"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="flex gap-1">
                <div className="w-6 h-6 rounded" style={{backgroundColor: '#0D1B2A'}}></div>
                <div className="w-6 h-6 rounded" style={{backgroundColor: '#5D6166'}}></div>
                <div className="w-6 h-6 rounded" style={{backgroundColor: '#1A2C42'}}></div>
              </div>
              <span className="font-semibold text-sm">Navy Blue</span>
              {settings.display_theme === 'navy' && <span className="ml-auto text-xs font-semibold" style={{color: '#0D1B2A'}}>Active</span>}
            </div>
            <p className="text-xs text-gray-500">Navy blue header (#0D1B2A) with grey accents (#5D6166). Cool, corporate look.</p>
          </button>
        </div>
        <p className="text-xs text-amber-600 mt-3">Remember to click "Save Settings" to persist the theme for all users.</p>
      </div>

      {/* Email Service (Resend) */}
      <div className="bg-white border border-gray-200 p-6" data-testid="email-service-section">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="w-5 h-5 text-[#C5A059]" />
          <h3 className="font-semibold text-[#1f1f1f]">Email Service (Resend)</h3>
          {emailStatus && (
            emailStatus.enabled ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5">
                <CheckCircle2 className="w-3 h-3" />ENABLED
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-700 bg-gray-100 border border-gray-300 px-2 py-0.5">
                <XCircle className="w-3 h-3" />NOT CONFIGURED
              </span>
            )
          )}
        </div>

        <p className="text-xs text-gray-500 mb-4">
          Configured via environment variables in <code className="bg-gray-100 px-1">backend/.env</code>.
          Password reset emails, admin notifications, and confirmations all flow through here.
        </p>

        {emailStatus && emailStatus.enabled && (
          <div className="grid grid-cols-2 gap-3 text-xs mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Sender email</p>
              <p className="font-medium">{emailStatus.sender_email || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Bank name</p>
              <p className="font-medium">{emailStatus.bank_name || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Support email</p>
              <p className="font-medium">{emailStatus.support_email || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Public base URL</p>
              <p className="font-medium">{emailStatus.public_base_url || '—'}</p>
            </div>
          </div>
        )}

        {emailStatus && emailStatus.enabled ? (
          <div className="border-t border-gray-100 pt-3">
            <p className="text-xs font-semibold text-gray-700 mb-2">Send a test email</p>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="your@email.com"
                value={emailTestTo}
                onChange={(e) => setEmailTestTo(e.target.value)}
                data-testid="email-test-target"
              />
              <Button
                onClick={handleEmailTest}
                disabled={emailTestBusy}
                className="bg-[#1f1f1f] hover:bg-[#333333] text-white whitespace-nowrap"
                data-testid="email-test-send-btn"
              >
                <Send className="w-4 h-4 mr-2" />{emailTestBusy ? 'Sending…' : 'Send Test'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
            To enable email, add these to <code>backend/.env</code> on the server and restart:
            <pre className="mt-2 bg-white border border-amber-200 p-2 text-[11px] overflow-x-auto">
RESEND_API_KEY=re_...
SENDER_EMAIL=info@beaconwoodpartners.com
BANK_NAME=Nkosi CBS
SUPPORT_EMAIL=info@beaconwoodpartners.com
PUBLIC_BASE_URL=https://cbs.beaconwoodpartners.com</pre>
          </div>
        )}
      </div>

      {/* Database Management */}
      <div className="bg-white border border-red-200 p-6" data-testid="database-management-section">
        <h2 className="flex items-center gap-2 text-sm font-bold text-red-700 mb-1">
          <AlertTriangle className="w-4 h-4" /> Database Management
        </h2>
        <p className="text-xs text-gray-500 mb-4">Export your data or clean the system to start fresh. Users, roles, and settings are always preserved.</p>

        <div className="flex gap-3">
          <Button
            onClick={handleExportDatabase}
            disabled={exporting}
            variant="outline"
            className="border-[#1f1f1f] text-[#1f1f1f]"
            data-testid="export-database-btn"
          >
            <Download className="w-4 h-4 mr-2" /> {exporting ? 'Exporting...' : 'Export Database (JSON)'}
          </Button>
          <Button
            onClick={() => setShowWipeDialog(true)}
            variant="outline"
            className="border-red-500 text-red-600 hover:bg-red-50"
            data-testid="open-wipe-dialog-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Clean System Data
          </Button>
        </div>
      </div>

      {/* Wipe Confirmation Dialog */}
      <Dialog open={showWipeDialog} onOpenChange={setShowWipeDialog}>
        <DialogContent className="max-w-lg" aria-describedby="wipe-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Clean System Data
            </DialogTitle>
          </DialogHeader>
          <p id="wipe-desc" className="sr-only">Confirmation dialog for cleaning transactional data</p>
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 text-sm text-red-800">
              <p className="font-bold mb-1">WARNING: This action cannot be undone!</p>
              <p className="text-xs">This will permanently delete all <strong>transactional records</strong> — clients, accounts, loans, transactions, journal entries, and audit logs — to give you a clean slate.</p>
            </div>

            {/* What stays vs what goes */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-green-50 border border-green-200 rounded">
                <p className="text-xs font-bold text-green-800 mb-2 flex items-center gap-1"><CheckSquare className="w-3 h-3" /> ALWAYS PRESERVED</p>
                <ul className="text-[11px] text-green-700 space-y-1">
                  <li>&#10003; User accounts & passwords</li>
                  <li>&#10003; Roles & permissions</li>
                  <li>&#10003; System settings & branding</li>
                  <li>&#10003; SARB rates & config</li>
                </ul>
              </div>
              <div className="p-3 bg-red-50 border border-red-200 rounded">
                <p className="text-xs font-bold text-red-800 mb-2 flex items-center gap-1"><Trash2 className="w-3 h-3" /> WILL BE DELETED</p>
                <ul className="text-[11px] text-red-700 space-y-1">
                  <li>&#10007; All client records</li>
                  <li>&#10007; Loans, savings, accounts</li>
                  <li>&#10007; Transactions & journal entries</li>
                  <li>&#10007; Notifications & audit logs</li>
                </ul>
              </div>
            </div>

            <div className="p-3 bg-amber-50 border border-amber-200 text-xs text-amber-800">
              <p className="font-semibold mb-1">Recommended: Export your data first</p>
              <Button size="sm" variant="outline" onClick={handleExportDatabase} disabled={exporting} className="mt-1 text-xs" data-testid="wipe-dialog-export-btn">
                <Download className="w-3 h-3 mr-1" /> {exporting ? 'Exporting...' : 'Download Database Export'}
              </Button>
            </div>

            <div className="space-y-3 p-3 bg-gray-50 border border-gray-200 rounded">
              <p className="text-xs font-semibold text-gray-700">Optional: Also clean these?</p>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-gray-600">Product definitions (loan, savings, shares, FD, RD, lease, charges)</Label>
                <Switch checked={wipeProducts} onCheckedChange={setWipeProducts} data-testid="wipe-products-toggle" />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-gray-600">Office / branch structure</Label>
                <Switch checked={wipeOffices} onCheckedChange={setWipeOffices} data-testid="wipe-offices-toggle" />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-red-700">Type CONFIRM WIPE to proceed:</Label>
              <Input
                value={wipeConfirm}
                onChange={(e) => setWipeConfirm(e.target.value)}
                placeholder="CONFIRM WIPE"
                className="mt-1 border-red-300 focus:border-red-500"
                data-testid="wipe-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowWipeDialog(false); setWipeConfirm(''); }} data-testid="cancel-wipe-btn">Cancel</Button>
            <Button
              onClick={handleWipeDatabase}
              disabled={wiping || wipeConfirm !== 'CONFIRM WIPE'}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-wipe-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> {wiping ? 'Cleaning...' : 'Clean System'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SystemSettingsPage;
